/**
 * Pure reconciliation engine.
 * No React, no side effects, no system clock.
 * Fixed anchor: new Date(Date.UTC(2026, 6, 24))
 */
import type { Requisition } from './mock-data';
import type { RegionalConnection } from './mock-data';
import type { ReconStatus } from './reconciliation-config';
import { defaultClassMap, onTrackBand, watchBand, overReportedBand,  } from './reconciliation-config';

export type { ReconStatus };

export interface ReconInput {
  requisitions: Requisition[];
  regional: RegionalConnection[];
  ratios: Record<string, number>;
  weights: { primary: number; capacity: number; secondary: number };
  lagDays: number;
}

export interface ReconRow {
  contractor: string;
  region: string;
  releasedUnits: number;
  releaseCount: number;
  inFlightCount: number;
  expectedByClass: { primary: number; capacity: number; secondary: number };
  classesPresent: number;
  expectedConnections: number;
  observedReported: number;
  observedVerified: number;
  variancePct: number;
  confidence: 'high' | 'medium' | 'low';
  status: ReconStatus;
  requisitionIds: string[];
  lineItems: LineItem[];
}

export interface LineItem {
  moenId: string;
  itemCode: string;
  quantity: number;
  ratio: number;
  contribution: number;
}

export interface ReconTotals {
  totalReleasedUnits: number;
  totalExpected: number;
  totalObservedReported: number;
  totalObservedVerified: number;
  netVariancePct: number;
}

const ANCHOR = new Date(Date.UTC(2026, 6, 24));
const MS_PER_DAY = 86400000;

function dateFromIso(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function computeStatus(variancePct: number): ReconStatus {
  if (variancePct >= -onTrackBand && variancePct <= onTrackBand) return 'on_track';
  if (variancePct < -onTrackBand && variancePct >= -watchBand) return 'watch';
  if (variancePct < -watchBand) return 'investigate';
  if (variancePct > onTrackBand && variancePct <= overReportedBand) return 'watch';
  return 'over_reported';
}

export function computeReconciliation(input: ReconInput): { rows: ReconRow[]; totals: ReconTotals } {
  const { requisitions, regional, ratios, weights, lagDays } = input;

  // Step 1: select released requisitions
  const released = requisitions.filter(
    (r) => r.status === 'released' && r.releaseDate !== null
  );

  const lagCutoff = new Date(ANCHOR.getTime() - lagDays * MS_PER_DAY);

  // Separate qualifying (past lag) from in-flight (within lag)
  const qualifying = released.filter(
    (r) => dateFromIso(r.releaseDate!).getTime() <= lagCutoff.getTime()
  );
  const inFlight = released.filter(
    (r) => dateFromIso(r.releaseDate!).getTime() > lagCutoff.getTime()
  );

  // Step 2: group by contractor
  const contractorMap = new Map<string, Requisition[]>();
  for (const req of qualifying) {
    const list = contractorMap.get(req.contractor) ?? [];
    list.push(req);
    contractorMap.set(req.contractor, list);
  }

  // Also track in-flight per contractor
  const inFlightMap = new Map<string, number>();
  for (const req of inFlight) {
    inFlightMap.set(req.contractor, (inFlightMap.get(req.contractor) ?? 0) + 1);
  }

  // Determine region per contractor (most recent qualifying release)
  const contractorRegion = new Map<string, string>();
  for (const [contractor, reqs] of contractorMap.entries()) {
    const sorted = [...reqs].sort(
      (a, b) => dateFromIso(b.releaseDate!).getTime() - dateFromIso(a.releaseDate!).getTime()
    );
    contractorRegion.set(contractor, sorted[0].region);
  }

  // Count contractors per region (for splitting observed)
  const regionContractorCount = new Map<string, number>();
  for (const region of contractorRegion.values()) {
    regionContractorCount.set(region, (regionContractorCount.get(region) ?? 0) + 1);
  }

  // Build rows
  const rows: ReconRow[] = [];

  for (const [contractor, reqs] of contractorMap.entries()) {
    const region = contractorRegion.get(contractor) ?? '';

    // Step 3: expected connections by class
    const byClass = { primary: 0, capacity: 0, secondary: 0 };
    const lineItems: LineItem[] = [];
    let releasedUnits = 0;

    for (const req of reqs) {
      const itemCode = req.itemCode ?? '';
      const ratio = ratios[itemCode] ?? 0;
      const cls = defaultClassMap[itemCode] ?? 'none';
      const contribution = req.quantityRequested * ratio;
      releasedUnits += req.quantityRequested;

      lineItems.push({
        moenId: req.moenId,
        itemCode,
        quantity: req.quantityRequested,
        ratio,
        contribution,
      });

      if (ratio > 0 && cls !== 'none') {
        byClass[cls as keyof typeof byClass] += contribution;
      }
    }

    // Step 4: blend with renormalisation
    const classesPresent = (
      (byClass.primary > 0 ? 1 : 0) +
      (byClass.capacity > 0 ? 1 : 0) +
      (byClass.secondary > 0 ? 1 : 0)
    );

    let expectedConnections = 0;
    if (classesPresent > 0) {
      // Renormalise weights for only present classes
      const rawWeights: Record<string, number> = {};
      if (byClass.primary > 0) rawWeights.primary = weights.primary;
      if (byClass.capacity > 0) rawWeights.capacity = weights.capacity;
      if (byClass.secondary > 0) rawWeights.secondary = weights.secondary;

      const weightSum = Object.values(rawWeights).reduce((s, w) => s + w, 0);
      const normWeights: Record<string, number> = {};
      for (const [k, w] of Object.entries(rawWeights)) {
        normWeights[k] = w / weightSum;
      }

      expectedConnections = Math.round(
        (byClass.primary * (normWeights.primary ?? 0)) +
        (byClass.capacity * (normWeights.capacity ?? 0)) +
        (byClass.secondary * (normWeights.secondary ?? 0))
      );
    }

    // Step 5: observed connections
    // Find earliest qualifying release date for this contractor
    const earliestRelease = reqs.reduce((earliest, r) => {
      const d = dateFromIso(r.releaseDate!).getTime();
      return d < earliest ? d : earliest;
    }, Infinity);

    // Map release date to monthIndex: find which connectionTracking month it corresponds to
    // connectionTracking months are: Aug 25 (idx 0) ... Jul 26 (idx 11)
    // We use the monthIndex from regional data
    // Earliest release date -> determine which monthIndex it falls on or after
    const MONTH_DATES = [
      Date.UTC(2025, 7, 1),  // Aug 25 -> idx 0
      Date.UTC(2025, 8, 1),  // Sep 25 -> idx 1
      Date.UTC(2025, 9, 1),  // Oct 25 -> idx 2
      Date.UTC(2025, 10, 1), // Nov 25 -> idx 3
      Date.UTC(2025, 11, 1), // Dec 25 -> idx 4
      Date.UTC(2026, 0, 1),  // Jan 26 -> idx 5
      Date.UTC(2026, 1, 1),  // Feb 26 -> idx 6
      Date.UTC(2026, 2, 1),  // Mar 26 -> idx 7
      Date.UTC(2026, 3, 1),  // Apr 26 -> idx 8
      Date.UTC(2026, 4, 1),  // May 26 -> idx 9
      Date.UTC(2026, 5, 1),  // Jun 26 -> idx 10
      Date.UTC(2026, 6, 1),  // Jul 26 -> idx 11
    ];

    // Find the first monthIndex where the month start >= earliestRelease
    let startMonthIndex = 0;
    for (let i = 0; i < MONTH_DATES.length; i++) {
      if (MONTH_DATES[i] >= earliestRelease) {
        startMonthIndex = i;
        break;
      }
    }

    const contractorsInRegion = regionContractorCount.get(region) ?? 1;

    const regionRecords = regional.filter(
      (rc) => rc.region === region && rc.monthIndex >= startMonthIndex
    );

    const totalReported = regionRecords.reduce((s, rc) => s + rc.reportedConnections, 0);
    const totalVerified = regionRecords.reduce((s, rc) => s + rc.verifiedConnections, 0);

    const observedReported = Math.round(totalReported / contractorsInRegion);
    const observedVerified = Math.round(totalVerified / contractorsInRegion);

    // Step 6: variance
    let variancePct = 0;
    let status: ReconStatus = 'on_track';

    if (expectedConnections === 0) {
      variancePct = 0;
      status = 'on_track';
    } else {
      variancePct = Math.round(
        ((observedReported - expectedConnections) / expectedConnections) * 1000
      ) / 10;
      // Step 7: status
      status = computeStatus(variancePct);
    }

    // Step 8: confidence
    let confidence: 'high' | 'medium' | 'low';
    if (classesPresent >= 3) {
      confidence = 'high';
    } else if (classesPresent === 2) {
      // Upgrade to high if primary is present
      confidence = byClass.primary > 0 ? 'high' : 'medium';
    } else {
      confidence = 'low';
    }

    rows.push({
      contractor,
      region,
      releasedUnits,
      releaseCount: reqs.length,
      inFlightCount: inFlightMap.get(contractor) ?? 0,
      expectedByClass: byClass,
      classesPresent,
      expectedConnections,
      observedReported,
      observedVerified,
      variancePct,
      confidence,
      status,
      requisitionIds: reqs.map((r) => r.moenId),
      lineItems,
    });
  }

  // Totals
  const totalReleasedUnits = rows.reduce((s, r) => s + r.releasedUnits, 0);
  const totalExpected = rows.reduce((s, r) => s + r.expectedConnections, 0);
  const totalObservedReported = rows.reduce((s, r) => s + r.observedReported, 0);
  const totalObservedVerified = rows.reduce((s, r) => s + r.observedVerified, 0);
  const netVariancePct =
    totalExpected === 0
      ? 0
      : Math.round(((totalObservedReported - totalExpected) / totalExpected) * 1000) / 10;

  return {
    rows,
    totals: {
      totalReleasedUnits,
      totalExpected,
      totalObservedReported,
      totalObservedVerified,
      netVariancePct,
    },
  };
}

export function getHeldContractors(rows: ReconRow[]): string[] {
  return rows.filter((r) => r.status === 'investigate').map((r) => r.contractor);
}

/**
 * Compute renormalised weights for display in the drawer.
 * Returns weights as percentages summing to 100.
 */
export function getRenormalisedWeights(
  byClass: { primary: number; capacity: number; secondary: number },
  weights: { primary: number; capacity: number; secondary: number }
): { primary: number; capacity: number; secondary: number } {
  const rawWeights: Record<string, number> = {};
  if (byClass.primary > 0) rawWeights.primary = weights.primary;
  if (byClass.capacity > 0) rawWeights.capacity = weights.capacity;
  if (byClass.secondary > 0) rawWeights.secondary = weights.secondary;

  const weightSum = Object.values(rawWeights).reduce((s, w) => s + w, 0);
  if (weightSum === 0) return { primary: 0, capacity: 0, secondary: 0 };

  return {
    primary: byClass.primary > 0 ? Math.round((rawWeights.primary / weightSum) * 100) : 0,
    capacity: byClass.capacity > 0 ? Math.round((rawWeights.capacity / weightSum) * 100) : 0,
    secondary: byClass.secondary > 0 ? Math.round((rawWeights.secondary / weightSum) * 100) : 0,
  };
}
